import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import {
  getMyAttendance,
  getEmployeeAttendance,
  getStudentAttendance,
  type AttendanceResponse,
  type AttendanceRecord,
} from "../../../service/own-attendance";
import TooltipOption from "../../../core/common/tooltipOption";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";

const MyAttendance = () => {
  const routes = all_routes;
  const currentBranch = useSelector(selectCurrentBranch);
  const componentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: componentRef });
  const [attendanceData, setAttendanceData] =
    useState<AttendanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(
    new Date().toISOString().slice(0, 7), // "YYYY-MM"
  );
  const { employeeId, enrollmentId } = useParams<{
    employeeId?: string;
    enrollmentId?: string;
  }>();

  // Fetch attendance data from API
  const fetchAttendance = async (month?: string) => {
    try {
      setLoading(true);
      setError(null);

      let data: AttendanceResponse;

      if (employeeId) {
        data = await getEmployeeAttendance({ employeeId, month });
      } else if (enrollmentId) {
        data = await getStudentAttendance({ enrollmentId, month });
      } else {
        data = await getMyAttendance({ month });
      }

      setAttendanceData(data);
    } catch (err) {
      setError("Failed to fetch attendance data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance(selectedMonth);
  }, [selectedMonth]);

  // Handle month change
  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    fetchAttendance(month);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "present":
        return "bg-success";
      case "absent":
        return "bg-danger";
      case "late":
        return "bg-warning";
      case "half-day":
        return "bg-dark";
      case "leave":
        return "bg-info";
      default:
        return "bg-secondary";
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "present":
        return "ti ti-checks";
      case "absent":
        return "ti ti-x";
      case "late":
        return "ti ti-clock-x";
      case "half-day":
        return "ti ti-calendar-event";
      case "leave":
        return "ti ti-clock-up";
      default:
        return "ti ti-help";
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "400px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger" role="alert">
            {error}
            <button
              className="btn btn-sm btn-primary ms-2"
              onClick={() => fetchAttendance(selectedMonth)}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {attendanceData?.person?.type === "student" ? "Student Attendance" : attendanceData?.person?.type === "employee" ? "Staff Attendance" : "My Attendance"}
            </h3>

            {attendanceData?.person?.name && (
              <div className="text-muted">
                <strong>{attendanceData.person.name}</strong>
                {attendanceData.person.code ? (
                  <span className="ms-2">({attendanceData.person.code})</span>
                ) : null}
              </div>
            )}
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  My Attendance
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <TooltipOption onExportPdf={handlePrint} />
          </div>
        </div>
        {/* /Page Header */}

        <div ref={componentRef} className="printable-content">
        <div className="print-only-header">
          <div className="school-brand">
            <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
            <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
          </div>
          <div className="report-title">
            {attendanceData?.person?.type === "student" ? "Student Attendance" : attendanceData?.person?.type === "employee" ? "Staff Attendance" : "My Attendance"}
            {attendanceData?.person?.name ? ` - ${attendanceData.person.name}` : ""}
          </div>
          <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
        </div>

        {/* Summary Cards */}
        {attendanceData && (
          <div className="row">
            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h3 className="text-success mb-1">
                        {attendanceData.report.summary.presentDays}
                      </h3>
                      <p className="mb-0">Present Days</p>
                    </div>
                    <div className="avatar avatar-sm bg-success-light">
                      <i className="ti ti-checks fs-4 text-success" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h3 className="text-danger mb-1">
                        {attendanceData.report.summary.absentDays}
                      </h3>
                      <p className="mb-0">Absent Days</p>
                    </div>
                    <div className="avatar avatar-sm bg-danger-light">
                      <i className="ti ti-x fs-4 text-danger" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h3 className="text-warning mb-1">
                        {attendanceData.report.summary.lateDays || 0}
                      </h3>
                      <p className="mb-0">Late Days</p>
                    </div>
                    <div className="avatar avatar-sm bg-warning-light">
                      <i className="ti ti-clock-x fs-4 text-warning" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h3 className="text-primary mb-1">
                        {attendanceData.report.summary.attendancePercentage}%
                      </h3>
                      <p className="mb-0">Attendance %</p>
                    </div>
                    <div className="avatar avatar-sm bg-primary-light">
                      <i className="ti ti-chart-pie fs-4 text-primary" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Controls */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
            <h4 className="mb-0">Attendance Records</h4>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <div className="input-icon-start position-relative">
                <input
                  type="month"
                  className="form-control"
                  value={selectedMonth}
                  onChange={(e) => handleMonthChange(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Attendance Legend */}
          <div className="card-body border-bottom">
            <div className="attendance-types">
              <ul className="attendance-type-list">
                <li>
                  <span className="attendance-icon bg-success">
                    <i className="ti ti-checks" />
                  </span>
                  Present
                </li>
                <li>
                  <span className="attendance-icon bg-danger">
                    <i className="ti ti-x" />
                  </span>
                  Absent
                </li>
                <li>
                  <span className="attendance-icon bg-pending">
                    <i className="ti ti-clock-x" />
                  </span>
                  Late
                </li>
                <li>
                  <span className="attendance-icon bg-dark">
                    <i className="ti ti-calendar-event" />
                  </span>
                  Half Day
                </li>
                <li>
                  <span className="attendance-icon bg-info">
                    <i className="ti ti-clock-up" />
                  </span>
                  Leave
                </li>
              </ul>
            </div>
          </div>

          {/* Attendance Records List */}
          <div className="card-body p-0">
            {attendanceData && attendanceData.data.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover table-center mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Late Minutes</th>
                      <th>Early Minutes</th>
                      <th>Working Hours</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceData.data.map((record: AttendanceRecord) => {
                      const checkIn = record.inOut?.find(
                        (io) => io.type === "in",
                      );
                      const checkOut = record.inOut?.find(
                        (io) => io.type === "out",
                      );

                      const formatTime = (timeString: string | undefined) => {
                        if (!timeString) return "-";
                        return new Date(timeString).toLocaleTimeString(
                          "en-US",
                          {
                            timeZone: "Asia/Karachi",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          },
                        );
                      };

                      const calculateWorkingHours = () => {
                        if (!checkIn?.time || !checkOut?.time) return "-";

                        const inTime = new Date(checkIn.time);
                        const outTime = new Date(checkOut.time);

                        if (outTime < inTime) {
                          outTime.setDate(outTime.getDate() + 1);
                        }

                        const diffMs = outTime.getTime() - inTime.getTime();
                        const hours = Math.floor(diffMs / (1000 * 60 * 60));
                        const minutes = Math.floor(
                          (diffMs % (1000 * 60 * 60)) / (1000 * 60),
                        );

                        if (hours === 0 && minutes === 0) return "-";
                        return `${hours}h ${minutes}m`;
                      };

                      const lateDisplay =
                        record.late?.actual > 0
                          ? `${record.late.effective} ${record.late.effective < record.late.actual
                            ? `(${record.late.actual})`
                            : ""
                          }`
                          : "-";

                      const earlyDisplay =
                        record.early?.actual > 0
                          ? `${record.early.effective} ${record.early.effective < record.early.actual
                            ? `(${record.early.actual})`
                            : ""
                          }`
                          : "-";

                      return (
                        <tr key={record._id ?? record.date}>
                          {/* Date */}
                          <td>
                            {(() => {
                              const date = new Date(record.date);
                              const formattedDate = date.toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              );
                              const dayName = date.toLocaleDateString("en-US", {
                                weekday: "long",
                              });
                              return `${formattedDate} (${dayName})`;
                            })()}
                          </td>

                          {/* Check In */}
                          <td>{formatTime(checkIn?.time)}</td>

                          {/* Check Out */}
                          <td>{formatTime(checkOut?.time)}</td>

                          <td className={record.late?.actual > 0 ? "text-warning fw-medium" : ""}>
                            {lateDisplay}
                          </td>

                          {/* Early Minutes (NEW) */}
                          <td className={record.early?.actual > 0 ? "text-info fw-medium" : ""}>
                            {earlyDisplay}
                          </td>

                          {/* Working Hours */}
                          <td className=" fw-semibold">
                            {calculateWorkingHours()}
                          </td>
                          <td>
                            <span
                              className={`badge ${getStatusColor(record.status)}`}
                            >
                              <i
                                className={`${getStatusIcon(record.status)} me-1`}
                              />
                              {record.status.charAt(0).toUpperCase() +
                                record.status.slice(1)}
                            </span>
                          </td>

                          {/* Remarks */}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-5">
                <i className="ti ti-calendar-off fs-1 text-muted mb-3" />
                <h5 className="text-muted">No attendance records found</h5>
                <p className="text-muted">
                  No attendance data available for the selected period.
                </p>
              </div>
            )}
          </div>

          {/* Performance Summary */}
          {attendanceData && (
            <div className="card-footer">
              <div className="row">
                <div className="col-md-6">
                  <h6 className="mb-1">Performance Summary</h6>
                  <p className="mb-0">
                    <strong>Overall Performance:</strong>{" "}
                    {attendanceData.report.overallPerformance}
                  </p>
                  <p className="mb-0">
                    <strong>Total Records:</strong> {attendanceData.count}
                  </p>
                  <p className="mb-0">
                    <strong>Total Late Minutes:</strong>{" "}
                    {attendanceData.report.summary.totalEffectiveLateMinutes}{" "}
                    ({attendanceData.report.summary.totalLateHours} hrs,{" "}
                    {attendanceData.report.summary.lateDeductionDays.toFixed(2)}{" "}
                    days deducted)
                  </p>
                  <p className="mb-0">
                    <strong>Total Early Minutes:</strong>{" "}
                    {attendanceData.report.summary.totalEffectiveEarlyMinutes}{" "}
                    ({attendanceData.report.summary.totalEarlyHours} hrs,{" "}
                    {attendanceData.report.summary.earlyDeductionDays.toFixed(2)}{" "}
                    days deducted)
                  </p>
                </div>
                <div className="col-md-6 text-end">
                  <div className="progress" style={{ height: "20px" }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{
                        width: `${attendanceData.report.summary.attendancePercentage}%`,
                      }}
                      aria-valuenow={parseFloat(
                        attendanceData.report.summary.attendancePercentage,
                      )}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      {attendanceData.report.summary.attendancePercentage}%
                    </div>
                  </div>
                  <small className="text-muted">Overall Attendance Rate</small>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default MyAttendance;
